import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

/**
 * The timesheet. Weeks, entries, the running timer and the approval slices.
 *
 * Rates are never sent and, for a non-owner, never returned: the API stamps them
 * server side and strips them on the way out. Nothing in here has a rate field
 * by design rather than by omission.
 */
export const TIME_KEY = ['time'] as const

export interface WeekEntry {
  id: string
  project_id: string
  project_name: string | null
  project_identifier: string | null
  task_id: string | null
  task_title: string | null
  entry_date: string
  minutes: number
  billable: number
  source: string
  note: string | null
  invoice_line_id: string | null
}

export type WeekStatus = 'open' | 'submitted' | 'partially_approved' | 'approved' | 'locked'

export interface WeekView {
  /** Set only when THIS week is empty and the person has time in another week,
   *  so the page can point at it rather than looking like there is nothing. */
  latest_with_time?: { week_start: string; minutes: number; total_minutes: number; entries: number } | null
  week: { id: string; week_start: string; user_id: string; status: WeekStatus; submitted_at: number | null; note: string | null }
  days: string[]
  entries: WeekEntry[]
  slices: Array<{ id: string; project_id: string; project_name: string | null; status: 'pending' | 'approved' | 'queried'; approved_by: string | null; approved_at: number | null; note: string | null }>
  total_minutes: number
  billable_minutes: number
  money_visible: boolean
  timer_ceiling_minutes: number
}

export interface RunningTimer {
  user_id: string
  project_id: string
  project_name: string | null
  project_identifier: string | null
  task_id: string | null
  task_title: string | null
  started_at: number
  note: string | null
  elapsed_minutes: number
  over_ceiling: boolean
}

export interface Suggestion {
  id: string
  source_kind: 'calendar' | 'meeting' | 'granola' | 'timer'
  source_id: string
  suggested_date: string
  suggested_minutes: number
  project_id: string | null
  project_name: string | null
  title: string | null
}

export interface Reconcile {
  week_start: string
  suggestions: Suggestion[]
  days: Array<{ date: string; evidence_minutes: number; logged_minutes: number; unaccounted_minutes: number }>
  evidence_minutes: number
  logged_minutes: number
  unaccounted_minutes: number
  /** Null when nothing was seen at all, which is NOT the same as zero. */
  coverage: number | null
}

export interface PendingSlice {
  id: string
  week_id: string
  week_start: string
  project_id: string
  project_name: string | null
  project_identifier: string | null
  person_user_id: string
  person_email: string | null
  status: 'pending' | 'approved' | 'queried'
  minutes: number
  billable_minutes: number
  note: string | null
  submitted_at: number | null
  /** S6's corroboration. Null means nothing was seen, rendered as "not
   *  measured" rather than as zero. */
  coverage: number | null
}

export function useReconcile(weekStart: string) {
  return useQuery({
    queryKey: [...TIME_KEY, 'reconcile', weekStart],
    queryFn: () => api.get<Reconcile>(`/time/reconcile?week_start=${weekStart}`),
    retry: false,
  })
}

export function useAcceptSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, project_id }: { id: string; project_id?: string }) =>
      api.post<{ entry_id: string }>(`/time/suggestions/${id}/accept`, { project_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TIME_KEY }),
  })
}

export function useDismissSuggestion() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ id: string }>(`/time/suggestions/${id}/dismiss`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: TIME_KEY }),
  })
}

export function useWeek(weekStart: string, userId?: string) {
  const qs = new URLSearchParams({ week_start: weekStart })
  if (userId) qs.set('user_id', userId)
  return useQuery({
    queryKey: [...TIME_KEY, 'week', weekStart, userId ?? 'me'],
    queryFn: () => api.get<WeekView>(`/time/week?${qs.toString()}`),
    retry: false,
  })
}

/** The header timer polls, because a timer started on a phone has to appear on
 *  the laptop without a reload. Thirty seconds is enough for a minutes display. */
export function useTimer() {
  return useQuery({
    queryKey: [...TIME_KEY, 'timer'],
    queryFn: () => api.get<{ timer: RunningTimer | null; ceiling_minutes: number }>('/time/timer'),
    refetchInterval: 30_000,
    retry: false,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: TIME_KEY })
}

export function useLogTime() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { project_id: string; task_id?: string | null; entry_date: string; duration: string; note?: string | null; billable?: boolean }) =>
      api.post<{ id: string; week_id: string }>('/time/entries', input),
    onSuccess: () => invalidate(qc),
  })
}

export function useUpdateEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; duration?: string; note?: string | null; billable?: boolean }) =>
      api.patch<{ id: string }>(`/time/entries/${id}`, input),
    onSuccess: () => invalidate(qc),
  })
}

export function useDeleteEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/time/entries/${id}`),
    onSuccess: () => invalidate(qc),
  })
}

export function useStartTimer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { project_id: string; task_id?: string | null; note?: string | null }) =>
      api.post<{ started: RunningTimer; banked: { id: string; minutes: number } | null }>('/time/timer/start', input),
    onSuccess: () => invalidate(qc),
  })
}

export function useStopTimer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { duration?: string } = {}) =>
      api.post<{ entry: { id: string; minutes: number } | null }>('/time/timer/stop', input),
    onSuccess: () => invalidate(qc),
  })
}

export function useSubmitWeek() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (weekStart: string) => api.post<{ week_id: string; slices: number }>('/time/weeks/submit', { week_start: weekStart }),
    onSuccess: () => invalidate(qc),
  })
}

export function useApprovals() {
  return useQuery({
    queryKey: [...TIME_KEY, 'approvals'],
    queryFn: () => api.get<{ slices: PendingSlice[] }>('/time/approvals').then((r) => r.slices),
    retry: false,
  })
}

export function useSliceAction(action: 'approve' | 'query' | 'reopen') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api.post<{ id: string; status?: string }>(`/time/approvals/${id}/${action}`, { note }),
    onSuccess: () => invalidate(qc),
  })
}
